import L from "leaflet"
import { fetchShapesBuffer } from "../lib/api"
import { formatClock, formatDelay, formatProgress } from "../lib/format"
import type { LiveVehicle, StopPoint } from "../lib/types"
import { parseShapeFile, polylineLatLngs } from "./shapeLoader"

type EnabledCheck = (routeId: string) => boolean
type VehicleClick = (vehicleId: string) => void
type RouteClick = (routeId: string) => void
type GetVehicles = () => LiveVehicle[]
type GetSelected = () => { routeId: string | null; vehicleId: string | null }
type GetRouteColor = (routeId: string, fallback?: string) => string

class MapController {
  map: L.Map | null = null
  private shapeLayers = new Map<string, L.Polyline>()
  private shapeRoutes = new Map<string, string>()
  private vehicleMarkers = new Map<string, L.CircleMarker>()
  private nextStopLabels = new Map<string, L.Marker>()
  private stopLayer = L.layerGroup()
  private stopMarkers = new Map<string, L.CircleMarker>()
  private isRouteEnabled: EnabledCheck = () => false
  private onVehicleClick: VehicleClick = () => {}
  private onRouteClick: RouteClick = () => {}
  private getVehicles: GetVehicles = () => []
  private getSelected: GetSelected = () => ({ routeId: null, vehicleId: null })
  private getRouteColor: GetRouteColor = (_routeId, fallback) => fallback || "ffffff"
  private dragHooked = false

  init(container: HTMLElement) {
    if (this.map) return this.map

    this.map = L.map(container, { zoomControl: false }).setView([47.603889, -122.33], 13)
    L.control.zoom({ position: "bottomright" }).addTo(this.map)

    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
      {
        maxZoom: 16,
        attribution: "Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ",
      }
    ).addTo(this.map)

    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 16, opacity: 0.85 }
    ).addTo(this.map)

    L.control.scale({ imperial: true, metric: true, position: "bottomleft" }).addTo(this.map)
    this.stopLayer.addTo(this.map)
    return this.map
  }

  bind(opts: {
    isRouteEnabled: EnabledCheck
    onVehicleClick: VehicleClick
    onRouteClick: RouteClick
    getVehicles: GetVehicles
    getSelected: GetSelected
    getRouteColor?: GetRouteColor
    onDragStart?: () => void
  }) {
    this.isRouteEnabled = opts.isRouteEnabled
    this.onVehicleClick = opts.onVehicleClick
    this.onRouteClick = opts.onRouteClick
    this.getVehicles = opts.getVehicles
    this.getSelected = opts.getSelected
    if (opts.getRouteColor) this.getRouteColor = opts.getRouteColor

    if (this.map && !this.dragHooked) {
      this.map.on("dragstart", () => opts.onDragStart?.())
      this.dragHooked = true
    }
  }

  invalidate() {
    this.map?.invalidateSize()
  }

  async loadShapes(colors: Record<string, string>, shapeRoutes: Record<string, string>) {
    if (!this.map) return
    const buffer = await fetchShapesBuffer()
    const shapes = parseShapeFile(buffer)

    for (const [id, layer] of this.shapeLayers) {
      layer.remove()
      this.shapeLayers.delete(id)
    }
    this.shapeRoutes.clear()

    for (const shape of shapes) {
      const routeId = shapeRoutes[shape.id]
      const color = colors[shape.id]
      const layer = L.polyline(polylineLatLngs(shape), {
        color: color ? `#${color}` : "#ffffff",
        weight: 3,
        opacity: 0.85,
      })

      if (routeId) {
        this.shapeRoutes.set(shape.id, routeId)
        layer.on("click", () => this.onRouteClick(routeId))
      }

      this.shapeLayers.set(shape.id, layer)
    }

    console.log(`loaded ${shapes.length} shapes`)
  }

  applyShapeColors(colors: Record<string, string>) {
    for (const [shapeId, layer] of this.shapeLayers) {
      const color = colors[shapeId]
      layer.setStyle({ color: color ? `#${color}` : "#ffffff" })
    }
  }

  syncShapes() {
    if (!this.map) return
    const selected = this.getSelected().routeId

    for (const [shapeId, layer] of this.shapeLayers) {
      const routeId = this.shapeRoutes.get(shapeId)
      const enabled = routeId ? this.isRouteEnabled(routeId) : false
      const isSelected = !!routeId && routeId === selected

      layer.setStyle({
        weight: isSelected ? 5 : 3,
        opacity: isSelected ? 1 : 0.8,
      })

      if (enabled) {
        if (!this.map.hasLayer(layer)) layer.addTo(this.map)
        if (isSelected) layer.bringToFront()
      } else if (this.map.hasLayer(layer)) {
        layer.remove()
      }
    }
  }

  fitRoute(routeId: string) {
    if (!this.map) return
    const layers = [...this.shapeLayers.entries()]
      .filter(([id]) => this.shapeRoutes.get(id) === routeId)
      .map(([, layer]) => layer)
    if (!layers.length) return
    this.map.fitBounds(L.featureGroup(layers).getBounds().pad(0.15))
  }

  private upsertNextStopLabel(vehicle: LiveVehicle, selected: boolean) {
    if (!this.map) return
    const existing = this.nextStopLabels.get(vehicle.id)

    if (!selected || !vehicle.nextStopName) {
      existing?.remove()
      this.nextStopLabels.delete(vehicle.id)
      return
    }

    const html = `<div class="next-stop-label"><span>next</span>${vehicle.nextStopName}</div>`
    if (!existing) {
      const icon = L.divIcon({
        className: "next-stop-wrap",
        html,
        iconSize: [180, 36],
        iconAnchor: [90, 42],
      })
      const marker = L.marker([vehicle.lat, vehicle.lon], { icon, interactive: false })
      marker.addTo(this.map)
      this.nextStopLabels.set(vehicle.id, marker)
    } else {
      existing.setLatLng([vehicle.lat, vehicle.lon])
      const el = existing.getElement()
      if (el) el.innerHTML = html
    }
  }

  syncVehicles() {
    if (!this.map) return
    const selectedId = this.getSelected().vehicleId
    const seen = new Set<string>()

    for (const vehicle of this.getVehicles()) {
      seen.add(vehicle.id)
      const colorHex = this.getRouteColor(vehicle.routeId, vehicle.routeColor)
      const color = colorHex ? `#${colorHex}` : "#66ccff"
      const selected = selectedId === vehicle.id
      let marker = this.vehicleMarkers.get(vehicle.id)

      if (!marker) {
        marker = L.circleMarker([vehicle.lat, vehicle.lon], {
          radius: selected ? 9 : 6,
          color: selected ? "#fff" : "#111",
          weight: selected ? 2 : 1,
          fillColor: color,
          fillOpacity: 0.95,
        })
        marker.on("click", (e) => {
          L.DomEvent.stopPropagation(e)
          this.onVehicleClick(vehicle.id)
        })
        this.vehicleMarkers.set(vehicle.id, marker)
      } else {
        marker.setLatLng([vehicle.lat, vehicle.lon])
        marker.setStyle({
          radius: selected ? 9 : 6,
          color: selected ? "#fff" : "#111",
          weight: selected ? 2 : 1,
          fillColor: color,
        })
      }

      const tip = [
        `<strong>${vehicle.routeName}</strong>${vehicle.headsign ? ` → ${vehicle.headsign}` : ""}`,
        formatDelay(vehicle.delay),
        vehicle.nextStopName ? `next: ${vehicle.nextStopName}` : null,
        vehicle.progress != null ? formatProgress(vehicle.progress) : null,
      ]
        .filter(Boolean)
        .join("<br>")

      marker.bindTooltip(tip, { direction: "top", opacity: 0.95 })

      const show = this.isRouteEnabled(vehicle.routeId)
      if (show) {
        if (!this.map.hasLayer(marker)) marker.addTo(this.map)
        if (selected) marker.bringToFront()
      } else if (this.map.hasLayer(marker)) {
        marker.remove()
      }

      this.upsertNextStopLabel(vehicle, selected)
    }

    for (const [id, marker] of this.vehicleMarkers) {
      if (!seen.has(id)) {
        marker.remove()
        this.vehicleMarkers.delete(id)
        this.nextStopLabels.get(id)?.remove()
        this.nextStopLabels.delete(id)
      }
    }

    for (const [id, label] of this.nextStopLabels) {
      if (id !== selectedId) {
        label.remove()
        this.nextStopLabels.delete(id)
      }
    }
  }

  clearStops() {
    this.stopLayer.clearLayers()
    this.stopMarkers.clear()
  }

  showStops(stops: StopPoint[], opts: { fit?: boolean; color?: string } = {}) {
    if (!this.map) return
    this.clearStops()
    const color = opts.color ? `#${opts.color}` : "#6ec3ff"
    const latLngs: L.LatLngExpression[] = []

    for (const stop of stops) {
      if (stop.lat == null || stop.lon == null) continue
      latLngs.push([stop.lat, stop.lon])

      const marker = L.circleMarker([stop.lat, stop.lon], {
        radius: stop.next ? 7 : 4,
        color: stop.next ? "#fff" : color,
        weight: stop.next ? 2 : 1,
        fillColor: stop.passed ? "#445066" : stop.next ? "#ffe08a" : color,
        fillOpacity: stop.passed ? 0.45 : 0.95,
      })

      const time = formatClock(stop.departure ?? stop.arrival)
      marker.bindTooltip(
        `<strong>${stop.name}</strong>${time !== "—" ? `<br>${time}` : ""}${stop.next ? "<br>next stop" : ""}`,
        { direction: "top", opacity: 0.95 }
      )
      marker.addTo(this.stopLayer)
      this.stopMarkers.set(stop.id, marker)
    }

    if (opts.fit && latLngs.length) {
      this.map.fitBounds(L.latLngBounds(latLngs).pad(0.2))
    }
  }

  focusStop(stopId: string) {
    const marker = this.stopMarkers.get(stopId)
    if (!marker || !this.map) return
    this.map.setView(marker.getLatLng(), Math.max(this.map.getZoom(), 15), { animate: true })
    marker.openTooltip()
  }

  focusVehicle(vehicle: LiveVehicle) {
    if (!this.map) return
    this.map.setView([vehicle.lat, vehicle.lon], Math.max(this.map.getZoom(), 14), { animate: true })
  }

  panTo(lat: number, lon: number) {
    this.map?.panTo([lat, lon], { animate: true })
  }
}

export const mapController = new MapController()
